import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Plus, Search, Trash2, XCircle } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { Modal } from "../../components/ui/Modal";
import { useSession } from "../../hooks/useSession";
import { customersAdminApi, type AdminCustomerListItem } from "../../api/customersAdminApi";
import { pbxRoutingApi, type CountryRules, type RouteCheckResult, type RoutingCountry } from "../../api/pbxRoutingApi";

const inputStyle: React.CSSProperties = { width: "100%", padding: "6px 8px", border: "1px solid var(--portal-border)", borderRadius: 6, fontSize: "0.85rem", fontFamily: "inherit", background: "white" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid var(--portal-border)", verticalAlign: "top" };

const ERRORS: Record<string, string> = {
  prefix_invalid: "Prefixes may only contain digits (separate them with commas).",
  prefixes_required: "Enter at least one allowed or blocked prefix.",
  country_code_invalid: "Invalid country code.",
  country_name_required: "Country name is required.",
};

const joinPrefixes = (p: string[]) => p.map((x) => (x === "" ? "(all)" : x)).join(", ");
const parsePrefixes = (s: string) =>
  s.split(/[,\s;]+/).map((x) => x.trim()).filter((x) => x.length > 0).map((x) => (x === "(all)" || x === "*" ? "" : x));

/**
 * IPPBX → Routing. Global list (admin edits, manager reads) and per-customer
 * overrides (admin: any customer; manager: own customers). Rules are
 * "allowed prefixes" and "specifically blocked prefixes" per country; the
 * PBX takes the longest matching prefix (customer rules first), deny wins
 * on ties, and a number without any rule is denied.
 */
export function PbxRoutingPage() {
  const { session } = useSession();
  const isAdmin = session?.kind === "Admin";
  const [globalRules, setGlobalRules] = useState<CountryRules[] | null>(null);
  const [countries, setCountries] = useState<RoutingCountry[]>([]);
  const [customers, setCustomers] = useState<AdminCustomerListItem[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerRules, setCustomerRules] = useState<CountryRules[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pbxRoutingApi.global().then(setGlobalRules).catch((e) => setError(e instanceof Error ? e.message : "Failed to load rules"));
    pbxRoutingApi.countries().then(setCountries).catch(() => setCountries([]));
    customersAdminApi.list().then((r) => setCustomers(r.items)).catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (customerId == null) {
      setCustomerRules(null);
      return;
    }
    let active = true;
    setCustomerRules(null);
    pbxRoutingApi.customer(customerId).then((r) => active && setCustomerRules(r)).catch((e) => active && setError(e instanceof Error ? e.message : "Failed to load overrides"));
    return () => {
      active = false;
    };
  }, [customerId]);

  const customer = customers.find((c) => c.customerId === customerId) ?? null;

  return (
    <>
      <PageHeader title="IPPBX routing" />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Which countries and regions PBX extensions may call. The PBX takes the longest matching prefix (a customer's own rules first, then the
          global list); when a prefix is both allowed and blocked it is blocked, and a number with no matching rule is not allowed.
        </p>
        {error && <div className="alert alert-error">{error}</div>}

        <RulesSection
          title="Global list"
          subtitle="Applies to every customer unless a customer override says otherwise."
          rules={globalRules}
          countries={countries}
          editable={isAdmin}
          onSave={(cc, body) => pbxRoutingApi.saveGlobal(cc, body).then(setGlobalRules)}
          onDelete={(cc) => pbxRoutingApi.deleteGlobal(cc).then(setGlobalRules)}
        />

        <section className="tile" style={{ display: "grid", gap: 12 }}>
          <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0 }}>Customer overrides</h3>
            <select className="portal-input-inline" style={{ ...inputStyle, maxWidth: 360 }} value={customerId ?? ""} onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Select a customer…</option>
              {customers
                .slice()
                .sort((a, b) => a.customerName.localeCompare(b.customerName))
                .map((c) => (
                  <option key={c.customerId} value={c.customerId}>{c.customerName}{c.enabled ? "" : " (disabled)"} · {c.managerName}</option>
                ))}
            </select>
          </header>
          {customerId == null ? (
            <div style={{ color: "var(--portal-muted)", fontSize: "0.9rem" }}>Choose a customer to see or add overrides. An override for a country replaces nothing globally; it only adds rules that win over the global ones for that customer.</div>
          ) : (
            <RulesSection
              title={`Overrides for ${customer?.customerName ?? customerId}`}
              subtitle="Rules here win over the global list for this customer (longest prefix first)."
              rules={customerRules}
              countries={countries}
              editable
              embedded
              onSave={(cc, body) => pbxRoutingApi.saveCustomer(customerId, cc, body).then(setCustomerRules)}
              onDelete={(cc) => pbxRoutingApi.deleteCustomer(customerId, cc).then(setCustomerRules)}
            />
          )}
        </section>

        <CheckSection customers={customers} defaultCustomerId={customerId} />
      </div>
    </>
  );
}

// ---- rules table --------------------------------------------------------------------

function RulesSection({
  title,
  subtitle,
  rules,
  countries,
  editable,
  embedded,
  onSave,
  onDelete,
}: {
  title: string;
  subtitle: string;
  rules: CountryRules[] | null;
  countries: RoutingCountry[];
  editable: boolean;
  embedded?: boolean;
  onSave: (countryCode: string, body: { countryName: string; allow: string[]; deny: string[] }) => Promise<unknown>;
  onDelete: (countryCode: string) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState<CountryRules | "new" | null>(null);
  const [deleting, setDeleting] = useState<CountryRules | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = (
    <>
      {subtitle && <div style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>{subtitle}</div>}
      {error && <div className="alert alert-error">{error}</div>}
      {rules == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : rules.length === 0 ? (
        <div style={{ color: "var(--portal-muted)", fontSize: "0.9rem" }}>No rules yet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>Country</th>
                <th style={thStyle}>Code</th>
                <th style={{ ...thStyle, minWidth: 260 }}>Allowed prefixes</th>
                <th style={{ ...thStyle, minWidth: 200 }}>Blocked specifically</th>
                {editable && <th style={thStyle} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.countryCode}>
                  <td style={{ ...tdStyle, fontWeight: 600, whiteSpace: "nowrap" }}>{r.countryName}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace" }}>+{r.countryCode}</td>
                  <td style={{ ...tdStyle, color: "var(--portal-status-success-darker)" }}>{r.allow.length ? joinPrefixes(r.allow) : <span style={{ color: "var(--portal-muted)" }}>—</span>}</td>
                  <td style={{ ...tdStyle, color: "#c0392b" }}>{r.deny.length ? joinPrefixes(r.deny) : <span style={{ color: "var(--portal-muted)" }}>—</span>}</td>
                  {editable && (
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", textAlign: "right" }}>
                      <button type="button" className="btn btn-secondary" onClick={() => setEditing(r)} style={{ padding: "4px 10px" }}>Edit</button>{" "}
                      <button type="button" className="btn btn-outline-danger" onClick={() => setDeleting(r)} style={{ padding: "4px 8px" }} aria-label="Remove country"><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <div>
          <button type="button" className="btn btn-primary" onClick={() => setEditing("new")} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Plus size={14} /> Add country
          </button>
        </div>
      )}
      <CountryRulesModal
        open={editing != null}
        existing={editing === "new" ? null : editing}
        taken={(rules ?? []).map((r) => r.countryCode)}
        countries={countries}
        busy={busy}
        onClose={() => setEditing(null)}
        onSave={async (cc, b) => {
          setBusy(true);
          setError(null);
          try {
            await onSave(cc, b);
            setEditing(null);
          } catch (err) {
            const code = err instanceof Error ? err.message : "";
            setError(ERRORS[code] ?? (err instanceof Error ? err.message : "Save failed"));
          } finally {
            setBusy(false);
          }
        }}
      />
      <ConfirmModal open={deleting != null} onClose={() => setDeleting(null)} title="Remove country" confirmLabel="Remove" busy={busy}
        onConfirm={async () => {
          if (!deleting) return;
          setBusy(true);
          setError(null);
          try {
            await onDelete(deleting.countryCode);
            setDeleting(null);
          } catch (err) {
            setError(err instanceof Error ? err.message : "Delete failed");
          } finally {
            setBusy(false);
          }
        }}
      >
        Remove all rules for <strong>{deleting?.countryName} (+{deleting?.countryCode})</strong>? Numbers in that country are then not allowed unless another rule matches.
      </ConfirmModal>
    </>
  );

  if (embedded) return <div style={{ display: "grid", gap: 10 }}>{body}</div>;
  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head"><h3>{title}</h3></header>
      {body}
    </section>
  );
}

function CountryRulesModal({
  open,
  existing,
  taken,
  countries,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  existing: CountryRules | null;
  taken: string[];
  countries: RoutingCountry[];
  busy: boolean;
  onClose: () => void;
  onSave: (countryCode: string, body: { countryName: string; allow: string[]; deny: string[] }) => Promise<void>;
}) {
  const [countryCode, setCountryCode] = useState("");
  const [countryName, setCountryName] = useState("");
  const [allow, setAllow] = useState("");
  const [deny, setDeny] = useState("");
  const [local, setLocal] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCountryCode(existing?.countryCode ?? "");
    setCountryName(existing?.countryName ?? "");
    setAllow(existing ? joinPrefixes(existing.allow) : "");
    setDeny(existing ? joinPrefixes(existing.deny) : "");
    setLocal(null);
  }, [open, existing]);

  const options = useMemo(
    () => countries.filter((c) => c.accessCode && (existing ? c.accessCode === existing.countryCode : !taken.includes(c.accessCode))),
    [countries, taken, existing],
  );

  const submit = async () => {
    if (!countryCode) return setLocal("Choose a country.");
    const a = parsePrefixes(allow);
    const d = parsePrefixes(deny);
    if (a.length === 0 && d.length === 0) return setLocal("Enter at least one allowed or blocked prefix (use (all) for the whole country).");
    if ([...a, ...d].some((p) => !/^\d*$/.test(p))) return setLocal("Prefixes may only contain digits.");
    setLocal(null);
    await onSave(countryCode, { countryName: countryName.trim(), allow: a, deny: d });
  };

  return (
    <Modal open={open} onClose={onClose} title={existing ? `${existing.countryName} (+${existing.countryCode})` : "Add country"} busy={busy} maxWidth={560}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {local && <div className="alert alert-error">{local}</div>}
        {!existing && (
          <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
            <span style={{ color: "var(--portal-muted)" }}>Country</span>
            <select className="portal-input-inline" style={inputStyle} value={countryCode} onChange={(e) => {
              const c = options.find((o) => o.accessCode === e.target.value);
              setCountryCode(e.target.value);
              setCountryName(c?.name ?? "");
            }}>
              <option value="">Select…</option>
              {options.map((c) => <option key={c.countryId} value={c.accessCode}>{c.name} (+{c.accessCode})</option>)}
            </select>
          </label>
        )}
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Allowed prefixes (after the country code, comma separated; "(all)" = whole country)</span>
          <textarea className="portal-input-inline" style={{ ...inputStyle, minHeight: 64, fontFamily: "monospace" }} value={allow} onChange={(e) => setAllow(e.target.value)} placeholder="1, 2, 3, 71, 72, 800" />
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Blocked specifically (wins over an allowed prefix it falls under)</span>
          <textarea className="portal-input-inline" style={{ ...inputStyle, minHeight: 48, fontFamily: "monospace" }} value={deny} onChange={(e) => setDeny(e.target.value)} placeholder="70, 76, 9, 118" />
        </label>
        <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>
          Example: allow <code>7</code> and block <code>70</code> lets an extension call +{countryCode || "44"} 71…79 but not +{countryCode || "44"} 70…
        </div>
      </div>
    </Modal>
  );
}

// ---- number check -------------------------------------------------------------------

function CheckSection({ customers, defaultCustomerId }: { customers: AdminCustomerListItem[]; defaultCustomerId: number | null }) {
  const [customerId, setCustomerId] = useState<number | null>(defaultCustomerId);
  const [number, setNumber] = useState("");
  const [result, setResult] = useState<RouteCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (defaultCustomerId != null) setCustomerId(defaultCustomerId);
  }, [defaultCustomerId]);

  const run = useCallback(async () => {
    if (customerId == null || !number.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await pbxRoutingApi.check(customerId, number.trim()));
    } catch (err) {
      setError(err instanceof Error ? (err.message === "number_invalid" ? "Enter a full international number, e.g. 0031201234567." : err.message) : "Check failed");
    } finally {
      setBusy(false);
    }
  }, [customerId, number]);

  return (
    <section className="tile" style={{ display: "grid", gap: 12 }}>
      <header className="tile-head"><h3>Test a number</h3></header>
      <form onSubmit={(e) => { e.preventDefault(); void run(); }} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Customer</span>
          <select className="portal-input-inline" style={{ ...inputStyle, minWidth: 240 }} value={customerId ?? ""} onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Select…</option>
            {customers.slice().sort((a, b) => a.customerName.localeCompare(b.customerName)).map((c) => <option key={c.customerId} value={c.customerId}>{c.customerName}</option>)}
          </select>
        </label>
        <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
          <span style={{ color: "var(--portal-muted)" }}>Number (international)</span>
          <input className="portal-input-inline" style={{ ...inputStyle, minWidth: 220 }} value={number} onChange={(e) => setNumber(e.target.value)} placeholder="+44 7700 900123" />
        </label>
        <button type="submit" className="btn btn-primary" disabled={busy || customerId == null || !number.trim()} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Search size={14} /> Check
        </button>
      </form>
      {error && <div className="alert alert-error">{error}</div>}
      {result && (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: 10, borderRadius: 8, border: `1px solid ${result.allowed ? "#a3d9b1" : "#e74c3c"}`, background: result.allowed ? "#e6f4ea" : "#fdecea", fontSize: "0.9rem" }}>
          {result.allowed ? <CheckCircle2 size={18} style={{ color: "#1e7e34", flexShrink: 0 }} /> : result.reason === "NO_RULE" ? <AlertTriangle size={18} style={{ color: "#c0392b", flexShrink: 0 }} /> : <XCircle size={18} style={{ color: "#c0392b", flexShrink: 0 }} />}
          <div>
            <strong>{result.number}</strong> is {result.allowed ? "allowed" : "not allowed"}.
            {result.reason === "NO_RULE" ? (
              <> No rule matches this number.</>
            ) : (
              <> Matched {result.scope} rule: {result.countryName} +{result.countryCode}{result.prefix ? ` ${result.prefix}` : " (all)"} → {result.action}.</>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
